import React from 'react';
import { Form, FormikProvider, useFormik } from "formik";
import { useMutation} from "@apollo/client";
import {
  CreateScopedRequestMutation,
  CreateScopedRequestMutationVariables,
  Request,
} from "lib/generated/graphql";
import { NEW_SCOPED_REQUEST_MUTATION } from "lib/mutations";
import { FIND_REQUESTS_QUERY } from "lib/queries";
import useAuth from "src/shared-components/Auth";
import { Button } from "src/shared-components";
import ScopeBuilder from "./ScopeBuilder";
import RolesBuilder from "./RolesBuilder";
import { ArrowBack } from "@material-ui/icons";
import ScopeTypeSelect from "./ScopeTypeSelect";
import EquitySelect from "./EquitySelect";

type ScopedEngagementProps = {
  onCreated: (request: Partial<Request>) => void;
}

const ScopedEngagement = ({ onCreated }: ScopedEngagementProps) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const [createRequestMutation, { error }] = useMutation<CreateScopedRequestMutation, CreateScopedRequestMutationVariables>(
    NEW_SCOPED_REQUEST_MUTATION,
    { refetchQueries: [FIND_REQUESTS_QUERY] },
  );

  const form = useFormik({
    initialValues: {
      text: '',
      roles: [],
      type: null,
      includeEquity: false,
    },
    onSubmit: async ({ text, roles, type, includeEquity }) => {
      setIsLoading(true);

      const companyId = user?.companies[0]?.id

      try {
        const { data } = await createRequestMutation({
          variables: {
            input: {
              text,
              roles,
              type,
              includeEquity,
              companyId,
            },
          },
        });

        setIsLoading(false);
        global?.analytics?.track(`Project Created`, {
          companyId,
        });
        onCreated(data.createScopedRequest);
      } catch (error) {
        /* istanbul ignore next */
        console.error(error);
        setIsLoading(false);
      }
    }
  });

  // TODO: useEffect track changes to all values
  //       then fire track events:
  //       template select - change template
  //       scope text, none -> some
  //       tags, add/remove
  //       scope type changed
  //       equity changed
  //       -> already done, created

  return (
    <div className="p-4 flex flex-col w-[59.7rem] text-huddleBlack">
      <div className="flex flex-col">
        <FormikProvider value={form}>
          <Form>
            {step === 1 && (
              <>
                <svg className="h-12 fill-current text-stone-400" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.85845 0H0V15.9431H4.85845V0Z" />
                  <path d="M4.85845 19.8008H0V36.0006H4.85845V19.8008Z" />
                  <path d="M13.9991 0H9.14062V15.9431H13.9991V0Z" />
                  <path d="M13.9991 19.8008H9.14062V36.0006H13.9991V19.8008Z" />
                  <path d="M9.13824 15.9434H4.85156V19.8005H9.13824V15.9434Z" />
                </svg>
                <div className="w-full border-b border-neutral-200 mt-6 mb-8 pb-4">
                  <h2 className="text-4xl">Let’s create a scope.</h2>
                </div>
                <div className="flex flex-col items-start space-y-8">
                  <ScopeBuilder />
                  <RolesBuilder />
                  <Button
                    roundness="full"
                    color="black"
                    size="lg"
                    type="button"
                    onClick={() => setStep(2)}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <div className="flex space-x-3 items-center mb-7">
                  <Button color="gray" onClick={() => setStep(1)} className="space-x-1">
                    <ArrowBack fontSize="small" />
                    <span>Back</span>
                  </Button>
                </div>
                <div className="flex flex-col items-start space-y-8">
                  <ScopeTypeSelect />
                  <EquitySelect />
                  <Button
                    roundness="full"
                    color="black"
                    size="lg"
                  >
                    Let&apos;s go
                  </Button>
                </div>
              </>
            )}
          </Form>
        </FormikProvider>
      </div>
    </div>
  )
}

export default ScopedEngagement;
